<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import QRCode from 'qrcode'
import { projectWorkspaceApi } from '@/api/index.js'
import { authStore } from '@/stores/auth.js'

const loading = ref(true)
const errorMessage = ref('')
const batch = ref(null)
const seats = ref([])
const assignments = ref([])
const tenantId = computed(() => authStore.state.selectedTenant?.id || '')
const project = computed(() => authStore.state.assignment?.project || null)
const currentSeatIds = computed(() => new Set(seats.value.map((seat) => seat.id)))
const assignedIds = computed(() => new Set(assignments.value.filter((item) => item.status === 'active' && currentSeatIds.value.has(item.persona_id)).map((item) => item.persona_id)))
const stateMessage = ref('正在读取云端人设矩阵…')
const inviteByPersona = ref({})
const generatingInvites = ref(false)
const generationProgress = ref(null)
const submitting = ref(false)
const progressConnectionLost = ref(false)
const automaticRecovery = computed(() => Boolean(generationProgress.value?.recovery?.automatic_pending || (generationProgress.value?.active && generationProgress.value?.recovery?.automatic_attempts > 0)))
const generationStopped = computed(() => !automaticRecovery.value && (['needs_attention', 'failed', 'cancelled'].includes(generationProgress.value?.status) || Boolean(generationProgress.value?.diagnostic)))
const generationComplete = computed(() => !generationStopped.value && generationProgress.value?.active === false && Number(generationProgress.value?.prepared_count || 0) >= Number(generationProgress.value?.total_count || 20))
const generationWaiting = computed(() => generationProgress.value?.status === 'waiting_approval')
const generationTitle = computed(() => generationWaiting.value ? '已准备好开始生成人设' : automaticRecovery.value ? (generationProgress.value?.recovery?.automatic_stage === 'waiting_model' ? '生成服务异常，已保留进度' : generationProgress.value?.recovery?.automatic_stage === 'waiting_budget' ? '系统正在恢复人设生成' : '正在自动修补人设') : generationStopped.value ? '本次人设生成暂时中断' : generationComplete.value ? '20 位人设已准备完成' : 'AI 项目经理正在准备人设')
const generationRecoveryMessage = computed(() => {
  const recovery = generationProgress.value?.recovery || {}
  if (recovery.automatic_stage === 'waiting_model') return '生成服务连续返回无效内容，已停止无效重复请求并记录故障。系统仍在检查恢复条件，恢复后自动接着完成，已通过的人设不会重做。'
  if (recovery.automatic_stage === 'waiting_budget') return '当前生成额度暂不可用，系统会自动检查，恢复后接着完成，已通过的人设会保留。'
  const strategies = Object.values(recovery.automatic_strategies || {}).map(value => value.strategy)
  if (strategies.includes('compact_rows')) return '前次修复未通过，正在调整生成方式并重新核对失败组，已通过的人设不会重做。'
  if (strategies.includes('independent_reconstruction')) return '已定位未通过的内容，正在根据报告重新准备失败组，已通过的人设不会重做。'
  return `系统会保留已通过的 ${generationProgress.value?.prepared_count || 0} 位，并自动检查、修补和完成剩余人设，无需重复点击。`
})
const generationPercent = computed(() => {
  const total = Number(generationProgress.value?.total_count || 20)
  const prepared = Number(generationProgress.value?.prepared_count || 0)
  return Math.max(0, Math.min(100, Math.round((prepared / total) * 100)))
})
let refreshGeneration = 0
let activeGenerationJobId = ''

function personaDisplayName(seat) {
  return seat?.profile?.app_persona_v13?.identity_label
    || seat?.identity_label
    || seat?.profile?.identity
    || seat?.name
    || '未命名人设'
}

const cacheKey = () => `pm-persona-matrix:${tenantId.value}:${project.value?.id || ''}`

function restoreLastSuccess() {
  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey()) || 'null')
    if (!cached || cached.tenantId !== tenantId.value || cached.projectId !== project.value?.id) return false
    batch.value = cached.batch || null
    seats.value = Array.isArray(cached.seats) ? cached.seats : []
    assignments.value = Array.isArray(cached.assignments) ? cached.assignments : []
    stateMessage.value = '正在更新人设状态。'
    return Boolean(batch.value)
  } catch { return false }
}

function saveLastSuccess(payload) {
  localStorage.setItem(cacheKey(), JSON.stringify({
    tenantId: tenantId.value,
    projectId: project.value.id,
    batch: payload.batch || null,
    seats: Array.isArray(payload.seats) ? payload.seats : [],
    assignments: Array.isArray(payload.assignments) ? payload.assignments : [],
    savedAt: new Date().toISOString(),
  }))
}

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds))

async function loadGenerationProgress(jobId = activeGenerationJobId) {
  if (!tenantId.value || !project.value?.id) return null
  try {
    const progress = await projectWorkspaceApi.personaGenerationProgress(tenantId.value, project.value.id, jobId)
    if (progress?.job_id) activeGenerationJobId = String(progress.job_id)
    if (progress?.job_id) generationProgress.value = progress
    progressConnectionLost.value = false
    return progress
  } catch {
    progressConnectionLost.value = true
    // The matrix snapshot remains usable if a separate progress read is delayed.
    return null
  }
}

async function load({ background = false } = {}) {
  if (!background) loading.value = !batch.value
  if (!background) errorMessage.value = ''
  const hadCachedData = Boolean(batch.value) || restoreLastSuccess()
  let lastError = null
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    try {
      const payload = await projectWorkspaceApi.personaMatrix(tenantId.value, project.value.id)
      const previousBatchId = String(batch.value?.id || '')
      batch.value = payload?.batch || null
      seats.value = Array.isArray(payload?.seats) ? payload.seats : []
      assignments.value = Array.isArray(payload?.assignments) ? payload.assignments : []
      if (previousBatchId && previousBatchId !== String(batch.value?.id || '')) inviteByPersona.value = {}
      saveLastSuccess(payload || {})
      if (!generationProgress.value) stateMessage.value = batch.value
        ? `已准备 ${seats.value.length} 位执行角色，${assignedIds.value.size} 位已分配到手机。`
        : '尚未准备执行角色。'
      loading.value = false
      return payload
    } catch (error) {
      lastError = error
      if (attempt < 3) {
        if (!background) stateMessage.value = '正在重新连接云端。'
        await wait(attempt * 1200)
      }
    }
  }
  errorMessage.value = hadCachedData
    ? '云端暂时没有响应，当前仍显示上次保存的人设。'
    : lastError?.message || '暂时无法读取执行角色。'
  loading.value = false
  return null
}

async function pollForRefreshedMatrix(batchId, previousBatchId, generation, jobId) {
  while (generation === refreshGeneration) {
    await wait(3000)
    if (generation !== refreshGeneration) return
    const progress = await loadGenerationProgress(jobId)
    if (generation !== refreshGeneration) return
    if (generationStopped.value) return
    if (progress && !progress.active && Number(progress.prepared_count || 0) >= Number(progress.total_count || 20)) {
      await load({ background: true })
      return
    }
    // Poll the small progress response, not the entire visible card list.
    if (progress && !progress.active && !progress.recovery?.automatic_pending) return
  }
}

async function refreshStatus() {
  const generation = ++refreshGeneration
  await load({ background: true })
  const progress = await loadGenerationProgress()
  if (generation === refreshGeneration && (progress?.active || progress?.recovery?.automatic_pending)) {
    void pollForRefreshedMatrix(progress.batch_id, batch.value?.id, generation, progress.job_id)
  }
}

async function refreshLatestMatrix() {
  if (loading.value || submitting.value || generationProgress.value?.active || automaticRecovery.value) return
  if (!window.confirm('AI 项目经理会使用最新一次完成的企业分析报告，准备新的 20 位执行角色。已有角色和历史分析都会保留。确认继续？')) return
  const generation = ++refreshGeneration
  const previousBatchId = String(batch.value?.id || '')
  submitting.value = true
  errorMessage.value = ''
  try {
    const refresh = await projectWorkspaceApi.refreshPersonaMatrix(tenantId.value, project.value.id)
    activeGenerationJobId = String(refresh?.job_id || '')
    const progress = await loadGenerationProgress(activeGenerationJobId)
    const payload = await load({ background: true })
    if (generation !== refreshGeneration) return
    if (generationStopped.value) return
    if (String(payload?.batch?.id || '') === String(refresh.batch_id || '')) {
      if (previousBatchId !== String(refresh.batch_id || '')) {
        await generateInvites({ expectedBatchId: refresh.batch_id, generatedFromRefresh: true })
      } else {
        stateMessage.value = '执行角色已准备完成。'
      }
      return
    }
    if (progress?.active || progress?.recovery?.automatic_pending) void pollForRefreshedMatrix(refresh.batch_id, previousBatchId, generation, activeGenerationJobId)
  } catch (error) {
    errorMessage.value = error?.message || '暂时无法准备执行角色，请稍后重试。'
  } finally { submitting.value = false }
}

async function continueGeneration() {
  if (submitting.value || !generationProgress.value?.recovery?.can_resume) return
  const jobId = generationProgress.value.job_id
  if (!jobId) return
  submitting.value = true
  errorMessage.value = ''
  const generation = ++refreshGeneration
  try {
    await projectWorkspaceApi.resumePersonaGeneration(tenantId.value, project.value.id, jobId)
    const progress = await loadGenerationProgress(jobId)
    if (generation === refreshGeneration && (progress?.active || progress?.recovery?.automatic_pending)) {
      void pollForRefreshedMatrix(progress.batch_id, batch.value?.id, generation, jobId)
    }
  } catch (error) {
    errorMessage.value = error?.message || '暂时无法继续，已保存的进度不会丢失。请刷新状态后查看。'
    await loadGenerationProgress(jobId)
  } finally { submitting.value = false }
}

function savePersonaQr(seat) {
  const invite = inviteByPersona.value[seat.id]
  if (!invite?.qrDataUrl) return
  const download = document.createElement('a')
  download.href = invite.qrDataUrl
  download.download = `AI营销助手_人设${String(seat.seat_no).padStart(2, '0')}_二维码.png`
  document.body.appendChild(download)
  download.click()
  download.remove()
  stateMessage.value = '已准备高清二维码图片，请在下载列表查看；图片保存后可从 AI 营销助手相册读取。'
}

async function generateInvites({ expectedBatchId = batch.value?.id, generatedFromRefresh = false } = {}) {
  if (!batch.value || generatingInvites.value) return
  generatingInvites.value = true
  errorMessage.value = ''
  stateMessage.value = generatedFromRefresh
    ? '正在准备手机扫码二维码…'
    : '正在为尚未领取的人设生成安全二维码…'
  try {
    const result = await projectWorkspaceApi.createPersonaInvites(tenantId.value, project.value.id, {
      expires_in_minutes: null,
      matrix_batch_id: expectedBatchId,
    })
    if (String(result?.matrix_batch_id || '') !== String(expectedBatchId || '')) {
      throw new Error('人设矩阵已经更新，请重新读取后再生成二维码。')
    }
    const pairs = []
    for (const item of (result?.items || [])) {
      pairs.push([
        String(item.persona_id),
        {
          ...item,
          qrDataUrl: await QRCode.toDataURL(item.qr_payload || item.deep_link, {
          width: 720,
          margin: 4,
          errorCorrectionLevel: 'M',
        }),
        },
      ])
    }
    inviteByPersona.value = Object.fromEntries(pairs)
    stateMessage.value = pairs.length
      ? `已准备 ${pairs.length} 个手机扫码二维码。`
      : '所有执行角色都已分配到手机。'
  } catch (error) {
    const unavailable = Number(error?.status || error?.response?.status || 0) === 502
    errorMessage.value = unavailable
      ? '手机二维码服务暂时不可用，请稍后再试。'
      : (error?.message || '手机二维码暂时无法生成，请稍后再试。')
    stateMessage.value = '执行角色资料保持不变。'
  } finally {
    generatingInvites.value = false
  }
}
onMounted(async () => {
  const generation = refreshGeneration
  await load()
  if (generation !== refreshGeneration) return
  const progress = await loadGenerationProgress()
  if (generation === refreshGeneration && (progress?.active || progress?.recovery?.automatic_pending)) void pollForRefreshedMatrix(progress.batch_id, batch.value?.id, generation, progress.job_id)
})
onBeforeUnmount(() => { refreshGeneration += 1 })
</script>

<template>
  <section class="matrix-page" aria-labelledby="matrix-title">
    <header class="matrix-header"><div><h1 id="matrix-title">20 位执行角色</h1><p>为企业准备内容执行角色，并分配给手机助手完成每日任务。</p></div><div class="matrix-actions"><button type="button" :disabled="loading || submitting || generationProgress?.active || automaticRecovery" @click="refreshLatestMatrix">{{ submitting || generationProgress?.active || automaticRecovery ? '正在准备人设…' : '生成 20 位执行角色' }}</button><button v-if="batch" class="primary-action" type="button" :disabled="loading || generatingInvites" @click="generateInvites">{{ generatingInvites ? '正在准备二维码…' : '生成手机二维码' }}</button></div></header>
    <section v-if="generationProgress" class="generation-progress" role="status" aria-live="polite">
      <div><strong>{{ generationTitle }}</strong><span>已准备 {{ generationProgress.prepared_count }}/{{ generationProgress.total_count }} 位（{{ generationPercent }}%）</span></div>
      <div class="progress-track" role="progressbar" aria-label="人设准备进度" :aria-valuenow="generationPercent" aria-valuemin="0" aria-valuemax="100"><span :style="{ width: `${generationPercent}%` }"></span></div>
      <p v-if="automaticRecovery">{{ generationRecoveryMessage }}</p>
      <p v-else-if="generationStopped">{{ generationProgress.recovery?.can_resume ? '已保存当前进度，点击继续即可接着完成。已准备好的人设不会重新生成。' : '已保存当前进度，暂时无法继续。已有的人设不会丢失。' }}</p>
      <p v-else-if="generationComplete">新的人设已准备完成。</p>
      <p v-else-if="generationWaiting">点击“生成 20 位执行角色”即可开始。</p>
      <p v-else>已准备的内容会保存，请稍候，无需重复点击。</p>
      <p v-if="progressConnectionLost">暂时无法更新进度，正在重新连接，已显示的进度会保留。</p>
      <p v-if="!generationComplete && batch">下方显示上次完成的人设，本次结果准备完成后再更新。</p>
      <div v-if="generationStopped && generationProgress.recovery?.can_resume" class="matrix-actions continue-actions"><button class="primary-action" type="button" :disabled="submitting" @click="continueGeneration">{{ submitting ? '正在继续…' : '继续生成人设' }}</button></div>
    </section>
    <p v-if="!generationProgress" class="matrix-state" aria-live="polite">{{ stateMessage }}</p>
    <div class="matrix-actions"><button type="button" :disabled="loading" @click="refreshStatus">刷新状态</button></div>
    <p v-if="errorMessage" class="form-error" role="alert">{{ errorMessage }}</p>
    <div v-if="loading && !batch" class="matrix-empty">正在读取云端人设矩阵…</div>
    <section v-else-if="!batch" class="matrix-empty"><strong>尚未准备执行角色</strong><p>生成后即可为手机助手分配角色。</p></section>
    <template v-else>
      <section class="seat-grid" aria-label="20 位执行角色"><article v-for="seat in seats" :key="seat.id"><div><span>角色 {{ String(seat.seat_no).padStart(2,'0') }}</span><strong :class="{ assigned: assignedIds.has(seat.id) }">{{ assignedIds.has(seat.id) ? '已分配到手机' : '待分配' }}</strong></div><h2>{{ personaDisplayName(seat) }}</h2><small>{{ seat.scenario || seat.profile?.first_person_context || '用于企业内容任务执行。' }}</small><div v-if="inviteByPersona[seat.id]" class="seat-qr"><img :src="inviteByPersona[seat.id].qrDataUrl" :alt="`角色${seat.seat_no}手机二维码`" /><strong>用 AI 营销助手扫码加入</strong><button type="button" @click="savePersonaQr(seat)" :aria-label="`保存角色${seat.seat_no}二维码图片`">保存二维码图片</button></div></article></section>
    </template>
  </section>
</template>

<style scoped>
.matrix-page{display:grid;gap:22px}.matrix-header{display:flex;justify-content:space-between;gap:24px;padding:28px;border:1px solid #dbe6ea;border-radius:18px;background:#fff}.matrix-header h1{margin:0;color:#17343b;font-size:34px}.matrix-header p{max-width:720px;color:#6b8087;line-height:1.75}.matrix-header button{align-self:start;min-height:42px;padding:0 17px;border:1px solid #bad8d5;border-radius:10px;color:#08746d;background:#edf8f7;font-weight:800}.matrix-actions{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:10px}.matrix-actions .primary-action{color:#fff;background:#0b8379}.generation-progress{padding:18px 20px;border:1px solid #b9ded8;border-radius:14px;background:#f1fbf9;color:#20444a}.generation-progress>div:first-child{display:flex;justify-content:space-between;gap:12px}.generation-progress span{font-weight:800;color:#08746d}.generation-progress p{margin:10px 0 0;color:#527278}.progress-track{height:10px;margin-top:12px;overflow:hidden;border-radius:999px;background:#d9ece9}.progress-track span{display:block;height:100%;border-radius:inherit;background:#0b8379;transition:width .35s ease}.matrix-state{margin:0;color:#627980}.matrix-empty{padding:54px;border:1px solid #dce7ea;border-radius:18px;background:#fff;text-align:center}.matrix-empty strong{font-size:20px}.matrix-empty p{color:#71868d}.seat-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px}.seat-grid article{min-width:0;padding:18px;border:1px solid #dce6e9;border-radius:14px;background:#fff}.seat-grid article>div{display:flex;justify-content:space-between;gap:8px;color:#798c92;font-size:11px}.seat-grid article>div strong{color:#9a6c18}.seat-grid article>div strong.assigned{color:#0b8379}.seat-grid h2{margin:15px 0 8px;color:#213d45;font-size:16px}.seat-grid small{display:block;margin-top:10px;color:#7b8e94;line-height:1.5}.seat-grid article>.seat-qr{display:grid;justify-items:center;gap:6px;margin-top:16px;padding:12px;border-radius:12px;background:#f0f8f7;text-align:center}.seat-qr img{display:block;width:180px;max-width:100%;height:auto}.seat-grid article>.seat-qr strong{color:#08746d;font-size:12px}@media(max-width:1100px){.seat-grid{grid-template-columns:repeat(2,1fr)}}@media(max-width:650px){.matrix-header{display:grid}.matrix-actions{justify-content:stretch}.matrix-actions button{flex:1}.seat-grid{grid-template-columns:1fr}}
.matrix-header>div:first-child{min-width:0;flex:1}
.generation-progress .progress-track{height:26px;border:1px solid #bad8d5;margin-top:16px}
.generation-progress>div:first-child{flex-wrap:wrap;align-items:center;font-size:18px}
.generation-progress .continue-actions{justify-content:flex-start;margin-top:16px}
@media(prefers-reduced-motion:reduce){.progress-track span{transition:none}}
.matrix-header>.matrix-actions{flex:0 1 320px;align-content:start}
.matrix-actions button{min-height:44px;padding:10px 16px;border:1px solid #bad8d5;border-radius:10px;color:#08746d;background:#edf8f7;font:inherit;font-weight:800;line-height:1.5;cursor:pointer;max-width:100%;overflow-wrap:anywhere}
.matrix-actions button:disabled{opacity:.65;cursor:wait}
.matrix-actions button:focus-visible{outline:3px solid #08746d;outline-offset:3px}
@media(max-width:900px){
  .matrix-header{display:grid;padding:20px;gap:16px}
  .matrix-header>.matrix-actions,.matrix-actions{display:grid;grid-template-columns:minmax(0,1fr);width:100%;justify-content:stretch}
  .matrix-actions button{width:100%;min-width:0;white-space:normal}
}
.seat-qr button {
  min-height: 42px;
  padding: 0 14px;
  border: 1px solid #bad8d5;
  border-radius: 8px;
  background: #fff;
  color: #08746d;
  font-weight: 700;
}
</style>
